/**
 * The console's view of the server.
 *
 * Every call is same-origin with `credentials: "include"`, because the session
 * is an HttpOnly cookie the script cannot read — which is the point: a bug in
 * this bundle cannot leak the session to anywhere it could be replayed from.
 */

import type { FontChoice, LauncherStyle, PresetId } from "../../chat-widget/src/presets";
import type { BillingState, PlanName, WorkspacePlan } from "./lib/plan";

export interface Operator {
  id: number;
  email: string;
  displayName: string;
  role: "owner" | "admin" | "agent";
  workspaceId: number;
  /** Returned by /agent/me; absent from the sign-in response until the next load. */
  avatarUrl?: string | null;
  color?: string;
  /** Console language; absent from the sign-in response until /agent/me is read. */
  locale?: "tr" | "en";
  /** Drives the Early access nav item; absent from the sign-in response until /agent/me is read. */
  isPlatformAdmin?: boolean;
  /** The status they last chose, so a reload does not put an operator who stepped away back in the queue. */
  presence?: "online" | "away";
}

/** Who wrote a message. The server never includes an e-mail address here. */
export interface OperatorInfo {
  id: number;
  displayName: string;
  avatarUrl: string | null;
  color: string;
}

export interface Attachment {
  id: number;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  url: string;
}

export interface ConversationRow {
  id: number;
  siteId: number;
  status: "pending" | "open" | "resolved" | "closed";
  assignedOperatorId: number | null;
  tags: string[];
  sourceUrl: string | null;
  rating: number | null;
  lastMessageAt: string;
  createdAt: string;
  visitorId: number;
  visitorName: string | null;
  visitorEmail: string | null;
  visitorVerifiedAt: string | null;
  visitorCountry: string | null;
  unread: number;
  siteName: string;
  /** `hasFile` is a PDF or text file the team sent; absent from an older server. */
  preview: { kind: Message["kind"]; text: string; hasImage: boolean; hasFile?: boolean } | null;
  assignee: OperatorInfo | null;
  /** Set while the visitor has the last word — someone is waiting for an answer. */
  waitingSince: string | null;
  closedAt?: string | null;
  /** In a search, the passage that matched. */
  snippet?: string | null;
}

export interface Stats {
  range: { days: number; timezone: string };
  totals: {
    conversations: number;
    unanswered: number;
    firstResponseAvg: number | null;
    firstResponseMedian: number | null;
    resolutionAvg: number | null;
    ratingAvg: number | null;
    ratingCount: number;
  };
  ratingDistribution: number[];
  perDay: Array<{ date: string; count: number }>;
  heatmap: number[][];
  operators: Array<OperatorInfo & { handled: number; firstResponseAvg: number | null; ratingAvg: number | null }>;
  topics: Array<{ tag: string; count: number }>;
  comments: Array<{ rating: number; comment: string; createdAt: string }>;
}

export interface InboxCounts {
  mine: number;
  unassigned: number;
  active: number;
  pending: number;
}

export interface TeamMember extends OperatorInfo {
  role: Operator["role"];
  presence: "online" | "away" | "offline";
}

export interface PageVisit {
  url: string;
  title: string | null;
  visitedAt: string;
}

export interface PastConversation {
  id: number;
  status: string;
  rating: number | null;
  createdAt: string;
  preview: string | null;
}

export interface CannedResponse {
  id: number;
  shortcut: string;
  title: string;
  body: string;
  usageCount: number;
  siteId: number | null;
}

export type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

export interface BusinessHours {
  timezone: string;
  weekly: Partial<Record<DayKey, Array<[string, string]>>>;
  offlineMessage: string;
  offlineEmailTo: string | null;
  holidayDates: string[];
}

export interface Trigger {
  id: number;
  siteId: number;
  name: string;
  event: "page_load" | "time_on_page" | "exit_intent";
  delaySeconds: number;
  urlPattern: string | null;
  message: string;
  onlyWhenOnline: boolean;
  enabled: boolean;
}

export type TriggerInput = Omit<Trigger, "id" | "siteId">;

export interface WidgetConfig {
  primaryColor: string;
  onPrimaryColor: string;
  position: "bottom-right" | "bottom-left";
  offsetX: number;
  offsetY: number;
  logoUrl: string | null;
  title: string;
  subtitle: string;
  placeholder: string;
  greeting: string;
  locale: "tr" | "en";
  preChatForm: boolean;
  /** Whether an anonymous visitor is asked for a name and address, and how firmly. */
  contactAsk: "off" | "optional" | "required";
  /** How long a finished chat stays in a returning visitor's widget. */
  endedChatShownFor: "none" | "1h" | "24h" | "7d" | "always";
  ratingEnabled: boolean;
  attachmentsEnabled: boolean;
  showTypingIndicator: boolean;
  soundEnabled: boolean;
  theme: "auto" | "light" | "dark";
  quickReplies: string[];
  faq: Array<{ q: string; a: string }>;
  /** The widget's look; the ids and shapes come from the widget itself (chat-widget/src/presets.ts). */
  preset: PresetId;
  /** Corner roundness in px, or null for the preset's own. */
  radius: number | null;
  font: FontChoice;
  launcherStyle: LauncherStyle;
  launcherText: string;
}

/** Spam rules for visitor messages; admin-only, never sent to a browser that is not the console. */
export interface Moderation {
  blockedWords: string[];
  blockLinks: boolean;
  maxRepeats: number;
}

/** The support ticket a chat was turned into, as the site reported it. */
export interface TicketRef {
  id: string;
  url: string | null;
  createdAt: string;
  operatorId: number;
}

export interface Workspace {
  id: number;
  name: string;
  autoAssign: "off" | "balanced";
}

/** "Get started" on the marketing site — see lib/chat-db/src/schema/early_access.ts. Platform-admin only. */
export interface EarlyAccessRequest {
  id: number;
  name: string;
  email: string;
  company: string | null;
  website: string | null;
  teamSize: string | null;
  plan: string | null;
  message: string | null;
  status: "new" | "contacted" | "invited" | "rejected";
  createdAt: string;
}

/** One workspace as the platform admin sees it — see the server's PlatformWorkspaceRow. */
export interface PlatformWorkspace {
  id: number;
  name: string;
  slug: string;
  createdAt: string;
  /** Effective plan: a suspended workspace reads "free" however it is stored. */
  plan: PlanName;
  storedPlan: PlanName;
  planStatus: string;
  graceUntil: string | null;
  currentPeriodEnd: string | null;
  /** True while Stripe owns this subscription: a manual change here can be undone by the next webhook. */
  hasSubscription: boolean;
  owner: { email: string; displayName: string } | null;
  usage: { sites: number; operators: number };
}

export interface AuditEntry {
  id: number;
  action: string;
  targetType: string | null;
  targetId: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  actorId: number | null;
  actorEmail: string | null;
  actorName: string | null;
  /** The actor was AgentlyDesk itself (a platform admin outside this workspace); no identity is sent. */
  actorPlatform: boolean;
}

/** Someone on a site right now, whether or not they have written (server ws/live.ts). */
export interface LiveVisitor {
  visitorId: number;
  siteId: number;
  siteName: string;
  displayName: string | null;
  email: string | null;
  country: string | null;
  /** The address of their latest visit. */
  ip: string | null;
  browser: string | null;
  os: string | null;
  device: string | null;
  identityVerified: boolean;
  since: string;
  page: { url: string; title: string | null } | null;
  tabs: number;
  /** Their open conversation, if they have one to go to. */
  conversationId: number | null;
}

export interface BannedVisitor {
  visitorId: number;
  displayName: string | null;
  email: string | null;
  siteId: number;
  siteName: string;
  reason: string | null;
  bannedAt: string | null;
}

export interface ConversationDetail {
  conversation: ConversationRow & { referrer: string | null; subject: string | null; ticketRef: TicketRef | null };
  messages: Message[];
  /** True when the thread is longer than the page that was sent; absent from an older server. */
  hasEarlier?: boolean;
  visitor: VisitorDetail | null;
  history: PastConversation[];
  site: { id: number; name: string; ticketsEnabled: boolean } | null;
  pages: PageVisit[];
}

export interface LinkSpan {
  start: number;
  end: number;
  href: string;
}

export interface Message {
  id: number;
  conversationId: number;
  kind: "visitor" | "operator" | "system" | "note" | "bot";
  operatorId: number | null;
  body: string;
  links: LinkSpan[];
  clientMsgId: string | null;
  createdAt: string;
  operator: OperatorInfo | null;
  attachments: Attachment[];
  /** Set once its author corrected it after sending. */
  editedAt?: string | null;
  readByVisitorAt?: string | null;
  pending?: boolean;
  /** 0..1 while an image is uploading. */
  progress?: number;
}

export interface VisitorDetail {
  id: number;
  displayName: string | null;
  email: string | null;
  identityVerified: boolean;
  externalId: string | null;
  country: string | null;
  /** The address of their latest visit; null for one recorded before addresses were kept. */
  ip: string | null;
  browser: string | null;
  os: string | null;
  device: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  isBanned: boolean;
  /** On the site at this moment; absent from an older server. */
  isLive?: boolean;
}

export interface Site {
  id: number;
  name: string;
  publicKey: string;
  secretKeyHint: string;
  /** Until when the replaced identity secret still verifies members; null when none is live. */
  previousKeyValidUntil: string | null;
  /** Null on a site made before webhook secrets existed. */
  webhookSecretHint: string | null;
  previousWebhookSecretValidUntil: string | null;
  allowedOrigins: string[];
  requireIdentityVerification: boolean;
  anonVisitorLimit: number;
  anonConversationLimit: number;
  anonWindowMinutes: number;
  widgetConfig: WidgetConfig;
  moderation: Moderation;
  ticketWebhookUrl: string | null;
  retentionDays: number;
  isActive: boolean;
  /**
   * How much of this site's attachment budget is spent. Null on the writes,
   * which do not pay for the aggregate — only the sites list and a single
   * site's read carry it.
   */
  storage: { usedBytes: number; capBytes: number } | null;
  embedSnippet: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api/v1${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(init.headers ?? {}),
    },
  });

  const parsed = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (!res.ok) {
    const body = parsed ?? {};
    throw new ApiError(res.status, (body["error"] as string) ?? `HTTP ${res.status}`, body);
  }
  // Every success carries a JSON object. Handing back {} for one that does not
  // would let each caller crash later on a missing field instead of failing here.
  if (parsed === null || typeof parsed !== "object") {
    throw new ApiError(res.status, `HTTP ${res.status}: unreadable response`, {});
  }
  return parsed as T;
}

export const api = {
  login: (email: string, password: string) =>
    call<{ ticket: string; needsTwoFactorSetup: boolean } | { operator: Operator; twoFactorSkipped: true }>("/agent/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  verify: (ticket: string, code?: string, recoveryCode?: string) =>
    call<{ operator: Operator }>("/agent/2fa/verify", {
      method: "POST",
      body: JSON.stringify({ ticket, code, recoveryCode }),
    }),

  setupTwoFactor: (ticket: string) =>
    call<{ secret: string; uri: string; qr: string }>("/agent/2fa/setup", {
      method: "POST",
      body: JSON.stringify({ ticket }),
    }),

  confirmTwoFactor: (ticket: string, code: string) =>
    call<{ recoveryCodes: string[] }>("/agent/2fa/setup/confirm", {
      method: "POST",
      body: JSON.stringify({ ticket, code }),
    }),

  /** `workspace` is the plan, limits and usage; older servers may omit it. */
  me: () => call<{ operator: Operator; workspace?: WorkspacePlan }>("/agent/me"),

  /** Public: the one-time link from an approval invite. Never signs anyone in. */
  setPassword: (token: string, password: string) =>
    call<{ ok: true }>("/agent/set-password", { method: "POST", body: JSON.stringify({ token, password }) }),

  /**
   * Public. Answers the same for an address that exists and one that does
   * not, so the screen can only ever say "if it is registered, we mailed it".
   */
  forgotPassword: (email: string) =>
    call<{ ok: true }>("/agent/forgot-password", { method: "POST", body: JSON.stringify({ email }) }),

  logout: () => call<{ ok: boolean }>("/agent/logout", { method: "POST" }),

  conversations: (params: Record<string, string | number | undefined>) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "") query.set(key, String(value));
    }
    return call<{ conversations: ConversationRow[]; nextBefore: number | null }>(
      `/agent/conversations?${query.toString()}`,
    );
  },

  conversation: (id: number) => call<ConversationDetail>(`/agent/conversations/${id}`),

  /** The page of a thread before the oldest message on screen. */
  earlierMessages: (id: number, before: number) =>
    call<{ messages: Message[]; hasEarlier: boolean }>(`/agent/conversations/${id}/messages?before=${before}`),

  /** Only the author may; the message comes back marked as edited. */
  editMessage: (conversationId: number, messageId: number, body: string) =>
    call<{ message: Message }>(`/agent/conversations/${conversationId}/messages/${messageId}`, {
      method: "PATCH",
      body: JSON.stringify({ body }),
    }),

  deleteMessage: (conversationId: number, messageId: number) =>
    call<{ ok: true }>(`/agent/conversations/${conversationId}/messages/${messageId}`, { method: "DELETE" }),

  inboxCounts: () => call<InboxCounts>("/agent/inbox/counts"),

  /** A preset, or two inclusive days in the site's own time zone. */
  stats: (range: "today" | "7d" | "30d" | { from: string; to: string }, siteId?: number) => {
    const query = new URLSearchParams(typeof range === "string" ? { range } : range);
    if (siteId) query.set("siteId", String(siteId));
    return call<Stats>(`/agent/stats?${query.toString()}`);
  },

  team: () => call<{ operators: TeamMember[] }>("/agent/team"),

  canned: () => call<{ canned: CannedResponse[] }>("/admin/canned"),

  useCanned: (id: number) => call<{ ok: true }>(`/agent/canned/${id}/use`, { method: "POST" }),

  sendTranscript: (id: number) =>
    call<{ ok: true; to: string }>(`/agent/conversations/${id}/transcript`, { method: "POST", body: JSON.stringify({}) }),

  sendMessage: (id: number, body: string, clientMsgId: string, note = false, mentions: number[] = []) =>
    call<{ message: Message; duplicate: boolean }>(`/agent/conversations/${id}/messages`, {
      method: "POST",
      body: JSON.stringify({ body, clientMsgId, note, ...(mentions.length ? { mentions } : {}) }),
    }),

  assign: (id: number, operatorId: number | null, note?: string) =>
    call<{ conversation: ConversationRow }>(`/agent/conversations/${id}/assign`, {
      method: "POST",
      body: JSON.stringify({ operatorId, ...(note ? { note } : {}) }),
    }),

  openTicket: (id: number) =>
    call<{ ticket: TicketRef }>(`/agent/conversations/${id}/ticket`, { method: "POST", body: JSON.stringify({}) }),

  workspace: () => call<{ workspace: Workspace }>("/admin/workspace"),

  updateWorkspace: (patch: Partial<Pick<Workspace, "autoAssign" | "name">>) =>
    call<{ workspace: Workspace }>("/admin/workspace", { method: "PATCH", body: JSON.stringify(patch) }),

  setStatus: (id: number, status: string) =>
    call<{ conversation: ConversationRow }>(`/agent/conversations/${id}/status`, {
      method: "POST",
      body: JSON.stringify({ status }),
    }),

  setTags: (id: number, tags: string[]) =>
    call<{ conversation: ConversationRow }>(`/agent/conversations/${id}/tags`, {
      method: "POST",
      body: JSON.stringify({ tags }),
    }),

  markRead: (id: number, upToId: number) =>
    call<{ ok: boolean }>(`/agent/conversations/${id}/read`, {
      method: "POST",
      body: JSON.stringify({ upToId }),
    }),

  banVisitor: (id: number, reason: string) =>
    call<{ ok: boolean }>(`/agent/visitors/${id}/ban`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  /** Lifts a ban: clears the flag and removes the entries the handshake checks. */
  unbanVisitor: (id: number) =>
    call<{ ok: boolean; bansRemoved: number }>(`/agent/visitors/${id}/ban`, { method: "DELETE" }),

  /** Refused (409 `visitor_verified`) for a member the site vouched for. An empty string clears a field. */
  updateVisitor: (id: number, patch: { displayName?: string; email?: string }) =>
    call<{ visitor: { id: number; displayName: string | null; email: string | null } }>(`/agent/visitors/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  bans: () => call<{ bans: BannedVisitor[] }>("/agent/bans"),

  liveVisitors: () => call<{ visitors: LiveVisitor[] }>("/agent/visitors/live"),

  /** The operator writes first; continues the visitor's open conversation if there is one. */
  startConversation: (visitorId: number, body: string, clientMsgId: string) =>
    call<{ conversationId: number; message: Message; duplicate: boolean }>(`/agent/visitors/${visitorId}/conversations`, {
      method: "POST",
      body: JSON.stringify({ body, clientMsgId }),
    }),

  /** Admin only, irreversible: the conversation, its messages and its images. */
  deleteConversation: (id: number) =>
    call<{ ok: true }>(`/agent/conversations/${id}`, { method: "DELETE" }),

  /** Admin only, irreversible: the person and everything they ever sent. */
  purgeVisitor: (id: number) =>
    call<{ ok: true; conversations: number; attachments: number; bansRemoved: number }>(
      `/admin/visitors/${id}/purge`,
      { method: "POST", body: JSON.stringify({}) },
    ),

  sites: () => call<{ sites: Site[] }>("/admin/sites"),

  createSite: (payload: { name: string; allowedOrigins: string[] }) =>
    call<{ site: Site; secretKey: string; webhookSecret: string }>("/admin/sites", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  updateSite: (id: number, payload: Record<string, unknown>) =>
    call<{ site: Site }>(`/admin/sites/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),

  /** Owner only, irreversible. The name is sent back so a request for the wrong site deletes nothing. */
  deleteSite: (id: number, confirmName: string) =>
    call<{ ok: true }>(`/admin/sites/${id}`, { method: "DELETE", body: JSON.stringify({ confirmName }) }),

  /** revokePrevious: the old key stops working now instead of after the 24-hour grace. */
  rotateKey: (id: number, revokePrevious = false) =>
    call<{ site: Site; secretKey: string; previousKeyValidUntil: string | null }>(
      `/admin/sites/${id}/rotate-key`,
      { method: "POST", body: JSON.stringify({ revokePrevious }) },
    ),

  revokePreviousKey: (id: number) =>
    call<{ site: Site }>(`/admin/sites/${id}/revoke-previous-key`, { method: "POST", body: JSON.stringify({}) }),

  rotateWebhookSecret: (id: number, revokePrevious = false) =>
    call<{ site: Site; webhookSecret: string; previousKeyValidUntil: string | null }>(
      `/admin/sites/${id}/rotate-webhook-secret`,
      { method: "POST", body: JSON.stringify({ revokePrevious }) },
    ),

  revokePreviousWebhookSecret: (id: number) =>
    call<{ site: Site }>(`/admin/sites/${id}/revoke-previous-webhook-secret`, {
      method: "POST",
      body: JSON.stringify({}),
    }),

  operators: () =>
    call<{
      operators: Array<
        Operator & { twoFactorEnabled: boolean; isActive: boolean; siteIds: number[]; presence: string }
      >;
    }>("/admin/operators"),

  /**
   * Without a password the person is mailed a link to choose their own
   * (`invited: true`). A 502 `mail_failed` still made the account — sending a
   * reset link is the retry.
   */
  createOperator: (payload: {
    email: string;
    password?: string;
    displayName: string;
    role: string;
    siteIds: number[];
  }) =>
    call<{ operator: Operator; invited?: boolean }>("/admin/operators", { method: "POST", body: JSON.stringify(payload) }),

  updateOperator: (id: number, payload: Record<string, unknown>) =>
    call<{ operator: Operator }>(`/admin/operators/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),

  /** Mails the teammate a link to choose a new password; the old one works until it is used. */
  resetOperatorPassword: (id: number) =>
    call<{ ok: true; email: string }>(`/admin/operators/${id}/reset-password`, { method: "POST", body: JSON.stringify({}) }),

  /** Ends their sessions and sends them through enrolment again at the next sign-in. */
  resetOperatorTwoFactor: (id: number) =>
    call<{ ok: true }>(`/admin/operators/${id}/reset-2fa`, { method: "POST", body: JSON.stringify({}) }),

  deleteOperator: (id: number) => call<{ ok: true }>(`/admin/operators/${id}`, { method: "DELETE" }),

  businessHours: (siteId: number) =>
    call<{ businessHours: BusinessHours | null }>(`/admin/sites/${siteId}/business-hours`),

  saveBusinessHours: (siteId: number, hours: BusinessHours) =>
    call<{ businessHours: BusinessHours }>(`/admin/sites/${siteId}/business-hours`, {
      method: "PUT",
      body: JSON.stringify(hours),
    }),

  triggers: (siteId: number) => call<{ triggers: Trigger[] }>(`/admin/sites/${siteId}/triggers`),

  createTrigger: (siteId: number, input: TriggerInput) =>
    call<{ trigger: Trigger }>(`/admin/sites/${siteId}/triggers`, { method: "POST", body: JSON.stringify(input) }),

  updateTrigger: (id: number, input: Partial<TriggerInput>) =>
    call<{ trigger: Trigger }>(`/admin/triggers/${id}`, { method: "PATCH", body: JSON.stringify(input) }),

  deleteTrigger: (id: number) => call<{ ok: true }>(`/admin/triggers/${id}`, { method: "DELETE" }),

  createCanned: (input: { shortcut: string; title: string; body: string; siteId: number | null }) =>
    call<{ canned: CannedResponse }>("/admin/canned", { method: "POST", body: JSON.stringify(input) }),

  updateCanned: (id: number, input: Partial<{ shortcut: string; title: string; body: string; siteId: number | null }>) =>
    call<{ canned: CannedResponse }>(`/admin/canned/${id}`, { method: "PATCH", body: JSON.stringify(input) }),

  deleteCanned: (id: number) => call<{ ok: true }>(`/admin/canned/${id}`, { method: "DELETE" }),

  changePassword: (current: string, next: string) =>
    call<{ ok: true }>("/agent/me/password", { method: "POST", body: JSON.stringify({ current, next }) }),

  regenerateRecoveryCodes: (code: string) =>
    call<{ recoveryCodes: string[] }>("/agent/me/recovery-codes", { method: "POST", body: JSON.stringify({ code }) }),

  updateProfile: (patch: { displayName?: string; locale?: "tr" | "en" }) =>
    call<{ operator: OperatorInfo & { locale: "tr" | "en" } }>("/agent/me", { method: "PATCH", body: JSON.stringify(patch) }),

  uploadAvatar: (file: Blob) =>
    call<{ avatarUrl: string }>("/agent/me/avatar", {
      method: "POST",
      body: file,
      headers: { "Content-Type": "application/octet-stream" },
    }),

  deleteAvatar: () => call<{ ok: true }>("/agent/me/avatar", { method: "DELETE" }),

  auditLog: (
    params: { action?: string; from?: string; to?: string; actorId?: number; before?: number; limit?: number } = {},
  ) => {
    const query = new URLSearchParams();
    if (params.action) query.set("action", params.action);
    if (params.from) query.set("from", params.from);
    if (params.to) query.set("to", params.to);
    if (params.actorId) query.set("actorId", String(params.actorId));
    if (params.before) query.set("before", String(params.before));
    if (params.limit) query.set("limit", String(params.limit));
    return call<{ entries: AuditEntry[]; nextBefore: number | null }>(`/admin/audit-log?${query.toString()}`);
  },

  /** Platform-admin only; a non-admin gets a 404 (see routes/platform.ts). */
  earlyAccess: (status?: EarlyAccessRequest["status"]) =>
    call<{ requests: EarlyAccessRequest[] }>(`/platform/early-access${status ? `?status=${status}` : ""}`),

  updateEarlyAccess: (id: number, status: EarlyAccessRequest["status"]) =>
    call<{ request: EarlyAccessRequest }>(`/platform/early-access/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),

  /**
   * Creates the workspace and its owner, and mails the set-password link.
   * A 502 `mail_failed` still made the account — resendInvite is the retry.
   */
  approveEarlyAccess: (id: number, payload: { plan: PlanName }) =>
    call<{ workspace: PlatformWorkspace; request: EarlyAccessRequest }>(`/platform/early-access/${id}/approve`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  platformWorkspaces: () => call<{ workspaces: PlatformWorkspace[] }>("/platform/workspaces"),

  /** `paused`/`resumed` list what a plan change switched off or back on. */
  updatePlatformWorkspace: (id: number, patch: { plan?: PlanName; planStatus?: "active" | "suspended" }) =>
    call<{
      workspace: PlatformWorkspace;
      paused: { sites: number[]; operators: number[] };
      resumed: { sites: number[]; operators: number[] };
    }>(`/platform/workspaces/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),

  /**
   * A customer opened by hand: workspace, owner and the invite mail. Like an
   * approval, a 502 `mail_failed` still made the account.
   */
  createPlatformWorkspace: (payload: { workspaceName: string; email: string; displayName: string; plan: PlanName }) =>
    call<{ workspace: PlatformWorkspace }>("/platform/workspaces", { method: "POST", body: JSON.stringify(payload) }),

  /** Irreversible. Refused for the admin's own workspace and for one Stripe is still billing. */
  deletePlatformWorkspace: (id: number, confirmSlug: string) =>
    call<{ ok: true }>(`/platform/workspaces/${id}`, { method: "DELETE", body: JSON.stringify({ confirmSlug }) }),

  resendInvite: (workspaceId: number) =>
    call<{ ok: true; email: string }>(`/platform/workspaces/${workspaceId}/resend-invite`, {
      method: "POST",
      body: JSON.stringify({}),
    }),

  /**
   * Web Push for this operator. `publicKey` is null when the server has no
   * VAPID pair, which is how the console knows to hide the setting.
   */
  pushState: () => call<{ publicKey: string | null; configured: boolean; devices: number }>("/agent/me/push"),

  subscribePush: (subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    call<{ ok: true; devices: number }>("/agent/me/push", { method: "POST", body: JSON.stringify(subscription) }),

  unsubscribePush: (endpoint: string) =>
    call<{ ok: true; removed: number; devices: number }>("/agent/me/push", {
      method: "DELETE",
      body: JSON.stringify({ endpoint }),
    }),

  /** The Telegram bridge: whether it is on, the team's group, this operator's own link. */
  telegramState: () =>
    call<{
      configured: boolean;
      botUsername: string | null;
      group: { title: string; linkedAt: string } | null;
      me: { telegramName: string; linkedAt: string } | null;
    }>("/agent/telegram"),

  telegramCode: (kind: "me" | "group") =>
    call<{ code: string; expiresAt: string }>(`/agent/telegram/${kind}/code`, { method: "POST", body: JSON.stringify({}) }),

  telegramUnlink: (kind: "me" | "group") =>
    call<{ ok: true }>(`/agent/telegram/${kind}`, { method: "DELETE", body: JSON.stringify({}) }),

  /** Owner only (403 otherwise); answers even while Stripe is unconfigured. */
  billing: () => call<BillingState>("/billing"),

  /** Both of these answer a Stripe URL to send the browser to. */
  checkout: (plan: "starter" | "team") =>
    call<{ url: string }>("/billing/checkout", { method: "POST", body: JSON.stringify({ plan }) }),

  billingPortal: () => call<{ url: string }>("/billing/portal", { method: "POST", body: JSON.stringify({}) }),
};

export function newClientMsgId(): string {
  return `a${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

/** Where the browser can download a conversation's transcript; the session cookie is the authorisation. */
export function transcriptUrl(conversationId: number): string {
  return `/api/v1/agent/conversations/${conversationId}/transcript.txt`;
}

/**
 * Uploads a file into a conversation: a picture, or a PDF or text file.
 *
 * XHR rather than fetch for the upload progress, which a large screenshot on a
 * slow line needs if the operator is not to assume it failed.
 */
export function uploadAttachment(
  conversationId: number,
  file: Blob,
  name: string,
  clientMsgId: string,
  note: boolean,
  onProgress?: (fraction: number) => void,
): Promise<Message> {
  const query = new URLSearchParams({ name, clientMsgId });
  if (note) query.set("note", "1");
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/v1/agent/conversations/${conversationId}/attachments?${query}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded / event.total);
    };
    xhr.onload = () => {
      let body: { message?: Message; error?: string } = {};
      try {
        body = JSON.parse(xhr.responseText) as typeof body;
      } catch {
        /* answered below */
      }
      if (xhr.status >= 200 && xhr.status < 300 && body.message) resolve(body.message);
      else reject(new Error(body.error ?? "upload failed"));
    };
    xhr.onerror = () => reject(new Error("network error"));
    xhr.send(file);
  });
}
